import React, { useContext, useLayoutEffect, useState } from 'react';
import { UserContext } from '../context/UserContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import signupImg from '../assets/login-img-mobile.png';
import logo from '../assets/Setu-Logo-mobile.png';
import menu from '../assets/menu-icon.svg';
import assessments from '../assets/assessments.svg';
import search from '../assets/search-line.svg';
import dashboards from '../assets/dashboard.svg';
import projects from '../assets/projects.svg';
import cssClass from './Login.module.css';
import { useEffect } from 'react';
import { changePageTitle } from '../utils/constants';
import { useNotification } from '../hooks/useNotification';
import { v4 as uuidv4 } from 'uuid';

/**
 * Login Component
 * This component handles user authentication using an OTP-based login system.
 *
 * Features:
 * - Users enter their email to receive an OTP.
 * - OTP is verified to log in the user.
 * - Redirects authenticated users to the intended page, default is landing page ('/').
 * - Uses `UserContext` for authentication state management.
 */
const Login = () => {
  const { showLoadingNotification, resolveNotification, rejectNotification } =
    useNotification();

  const [loginData, setLoginData] = useState({});
  const [showOTPInput, setShowOTPInput] = useState(false);
  const [showResend, setShowResend] = useState(false);
  const [error, setError] = useState('');
  const [timer, setTimer] = useState(0);
  const [otpSent, setOtpSent] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [isButtonDisable, setIsButtonDisable] = useState(false);
  const { isLoggedIn, fetchUserDetails } = useContext(UserContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  /**
   * Effect to check if the user is already logged in.
   * If authenticated, the user is redirected to the intended page.
   */
  useLayoutEffect(() => {
    if (isLoggedIn) {
      const continueURL = searchParams.get('continueURL');

      if (continueURL && continueURL !== 'null') {
        window.location.href = continueURL;
      } else {
        navigate('/');
      }
    }
  }, [isLoggedIn]);

  /**
   * Handles input field changes and updates the state.
   * @param {Object} event - Input change event
   */
  const handleChange = async (event) => {
    const { name, value } = event.target;
    setLoginData({
      ...loginData,
      [name]: name === 'email' ? value.toLowerCase() : value,
    });
  };

  /**
   * Sends an OTP request to the server for user authentication.
   * @param {Object} event - Form submission event
   */
  const sendOTP = async (event) => {
    event?.preventDefault();

    // email validation
    if (!loginData?.email) {
      setError('Email is required.');
      return;
    }

    if (!/\S+@\S+\.\S+/.test(loginData?.email)) {
      setError('Please enter a valid email address.');
      return;
    }

    const toastId = uuidv4();
    showLoadingNotification(toastId, 'Sending OTP. Please wait');
    try {
      setIsButtonDisable(true);
      
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/u/login-request`,
        {
          method: 'POST',
          mode: 'cors',
          cache: 'no-cache',
          // credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(loginData),
        },
      );
      const responseData = await response.json();
      if (response.status === 200) {
        setShowOTPInput(true);
        setOtpSent(true);
        startCountdown();
        resolveNotification(toastId, 'OTP sent successfully to your email');
      } else {
        setError(responseData.message);
        rejectNotification(toastId, 'Something went wrong. Please try again!');
      }
    } catch (error) {
      console.log(error);
      rejectNotification(toastId, 'Something went wrong. Please try again!');
    } finally {
      setIsButtonDisable(false);
    }
  };

  /**
   * Verifies the OTP entered by the user and completes the login process.
   */
  const verifyOTP = async () => {
    const toastId = uuidv4();
    showLoadingNotification(toastId, 'Verifying OTP. Please wait');
    try {
      const data = {
        email: loginData.email,
        otp: loginData.otp,
      };
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/u/verify-login`,
        {
          method: 'POST',
          mode: 'cors',
          cache: 'no-cache',
          // credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        },
      );
      const responseData = await response.json();
      if (response.status === 200) {
        setIsVerified(true);
        localStorage.setItem('u_e', loginData.email);
        localStorage.setItem('session_token', responseData?.session_token);
        // await new Promise((resolve) => setTimeout(resolve, 2000));
        resolveNotification(toastId, 'OTP verified successfully!!');
        await fetchUserDetails();
        const continueURL = searchParams.get('continueURL');
        
        if (continueURL && continueURL !== 'null') {
          window.location.href = continueURL;
        } else {
          navigate('/');
        }
      } else {
        setError(responseData?.message);
        rejectNotification(toastId, 'Something went wrong. Please try again!');
      }
    } catch (error) {
      rejectNotification(toastId, 'Something went wrong. Please try again!');
      console.log(error);
    }
  };

  /**
   * Starts the countdown after which resend OTP button will appear.
   */
  const startCountdown = () => {
    setTimer(90);
    setShowResend(false);
  };

  /**
   * Function to resend OTP
   */
  const handleResendOTP = async() => {
    setLoginData({...loginData, otp: ''});
    await sendOTP();
    setShowResend(false);
  };

  /**
   * Effect to automatically verify OTP once the user has entered 6 digits.
   */
  useEffect(() => {
    if (showOTPInput && loginData?.otp.length === 6) {
      verifyOTP();
    }
  }, [loginData]);

  /**
   * Effect to update the page title when the component mounts.
   */
  useEffect(() => {
    changePageTitle('Login');
  }, []);

  useEffect(() => {
    if (timer > 0) {
      const timeout = setTimeout(() => setTimer(timer - 1), 1000);
      return () => clearTimeout(timeout);
    } else if (otpSent) {
      setShowResend(true);
      setShowOTPInput(false); // Hide OTP input when timer ends
    }
  }, [timer]);

  return (
    <>
      <div className={cssClass['container']}>
        <div className={cssClass['header-div']}>
          <div className={cssClass['header-logo']}>
            <img src={logo} alt="" />
          </div>
          <div className={cssClass['header-right']}>
            <div className={cssClass['free-assment']}>Take Free Assessment</div>
            <div className={cssClass['login']}>login</div>
            <div className={cssClass['']}>
              <img src={menu} alt="" />
            </div>
          </div>
        </div>
        <div className={cssClass['main-div']}>
          <div className={cssClass['img-div']}>
            <img src={signupImg} alt="" className={cssClass['img']} />
          </div>
          <div className={cssClass['content-div']}>
            <div className={cssClass['content']}>
              <div className={cssClass['login-content']}>
                <h2 className={cssClass['login-heading']}>
                  Login {error && <p className={cssClass['error']}>{error}</p>}
                </h2>
                <form onSubmit={sendOTP}>
                  <div className={cssClass['form-group-email']}>
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      className={cssClass['inp-box']}
                      value={loginData.email}
                      onChange={handleChange}
                      disabled={showOTPInput}
                    />
                  </div>

                  {showOTPInput && (
                    <div className={cssClass['form-group-otp']}>
                      <label htmlFor="otp">Enter OTP</label>
                      <input
                        type="number"
                        name="otp"
                        id="otp"
                        className={`${
                          isVerified
                            ? cssClass['inp-box-green']
                            : cssClass['inp-box']
                        }`}
                        value={loginData.otp}
                        onChange={handleChange}
                        maxLength={6} // Restrict input to 6 digits
                        inputMode="numeric"
                      />
                      {timer > 0 && (
                        <div className={cssClass['timer-text-div']}>
                          <span className={cssClass['timer-text']}>
                            {timer + ' '}Sec
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className={cssClass['button-group']}>
                    {!otpSent && (
                      <button
                        type="submit"
                        className={cssClass['login-btn']}
                        disabled={isButtonDisable}
                      >
                        {!isButtonDisable ? 'Get OTP' : 'Sending ...'}
                      </button>
                    )}
                    {showResend && (
                    <button
                      className={cssClass["login-btn"]}
                      onClick={handleResendOTP}
                      disabled={isButtonDisable}
                    >
                      Resend OTP
                    </button>
                  )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
        <div className={cssClass['footer']}>
          <div className={cssClass['footer-div']}>
            <div className={cssClass['footer-function']}>
              <div className={cssClass['footer-assessment']}>
                <img src={assessments} alt="" />
                <div className={cssClass['free']}>Free</div>
              </div>

              <p> Assessment</p>
            </div>
            <div className={cssClass['footer-function']}>
              <img src={projects} alt="" />
              <p>Projects</p>
            </div>
            <div className={cssClass['footer-function']}>
              <img src={search} alt="" />
              <p>Search</p>
            </div>
            <div className={cssClass['footer-function']}>
              <img src={dashboards} alt="" />
              <p>Dashboard</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
